/**
 * The twelve states, their copy, and how each one is actually reached now that
 * the real ShieldWall widget drives the flow.
 *
 * `trigger` records the live signal. Two states (4, 8) are design-review only:
 * the widget renders its own progress UI for 4 inside our slot, and 8 only
 * surfaces if a silent token re-issue also fails.
 *
 * `tag` is a short text label printed next to every status sentence, so state
 * is never carried by colour alone.
 */

export type StateId =
  | "sdk-loading"
  | "passive"
  | "challenge"
  | "running"
  | "passed"
  | "failed"
  | "offline"
  | "expired"
  | "limited"
  | "submitting"
  | "submitted"
  | "rejected";

/** Value written to `data-state` on the reserved slot. */
export type SlotSkin =
  | "loading"
  | "passive"
  | "challenge"
  | "running"
  | "passed"
  | "failed"
  | "offline"
  | "expired"
  | "limited";

export type ActId = "retry" | "report" | "rerun" | "reload";

export interface Act {
  id: ActId;
  label: string;
  variant: "ghost" | "report";
}

export interface Note {
  tag: string;
  text: string;
  tone: "ok" | "bad";
}

export interface StateSpec {
  n: number;
  id: StateId;
  title: string;
  slot: SlotSkin;
  tag: string;
  status: string;
  /** Route through role="alert" rather than role="status". */
  alert: boolean;
  acts: Act[];
  /** Keep the real widget mounted and visible in the slot. */
  showWidget: boolean;
  submitLabel: string;
  submitEnabled: boolean;
  submitBusy: boolean;
  /** Why submit is refusing, spoken on an attempted click. */
  blockedReason?: string;
  note?: Note;
  countdown?: boolean;
  /** How this state is reached in live operation. */
  trigger: string;
}

const POST = "Post to the wall";

export const STATES: readonly StateSpec[] = [
  {
    n: 1,
    id: "sdk-loading",
    title: "SDK loading",
    slot: "loading",
    tag: "Setting up",
    // Also one line, for the same reason state 3's is.
    status: "Getting the check ready.",
    alert: false,
    acts: [],
    showWidget: true,
    submitLabel: POST,
    submitEnabled: false,
    submitBusy: false,
    blockedReason: "The check is still setting up. Give it a second.",
    trigger: "widget mounted, before the first onResult or onError",
  },
  {
    n: 2,
    id: "passive",
    title: "Passive pass",
    slot: "passive",
    tag: "Clear",
    status: "Nothing needed from you.",
    alert: false,
    acts: [],
    showWidget: false,
    submitLabel: POST,
    submitEnabled: true,
    submitBusy: false,
    trigger: "onVerified with no preceding onChallengeRequired",
  },
  {
    n: 3,
    id: "challenge",
    title: "Challenge required",
    slot: "challenge",
    tag: "One step",
    // One line, and it stays one line at 375px — every extra line here is a
    // line added to the reserved height that the other 95% sit and look at.
    status: "Our check was inconclusive.",
    alert: false,
    acts: [],
    showWidget: true,
    submitLabel: POST,
    submitEnabled: false,
    submitBusy: false,
    blockedReason: "Finish the check above first, then post.",
    trigger: "onChallengeRequired, or onResult with verdict CHALLENGE_REQUIRED",
  },
  {
    n: 4,
    id: "running",
    title: "Challenge running",
    slot: "running",
    tag: "Working",
    status: "Running the check.",
    alert: false,
    acts: [],
    showWidget: true,
    submitLabel: POST,
    submitEnabled: false,
    submitBusy: false,
    blockedReason: "The check is still running.",
    trigger: "design review only — the widget renders its own progress inside the slot",
  },
  {
    n: 5,
    id: "passed",
    title: "Challenge passed",
    slot: "passed",
    tag: "Cleared",
    status: "That did it. Your post is ready to go.",
    alert: false,
    acts: [],
    showWidget: false,
    submitLabel: POST,
    submitEnabled: true,
    submitBusy: false,
    trigger: "onVerified after onChallengeRequired",
  },
  {
    n: 6,
    id: "failed",
    title: "Challenge failed",
    slot: "failed",
    tag: "Blocked",
    status: "The check did not clear, so posting is held.",
    alert: true,
    acts: [
      { id: "retry", label: "Run it again", variant: "ghost" },
      { id: "report", label: "It got me wrong", variant: "report" },
    ],
    showWidget: false,
    submitLabel: POST,
    submitEnabled: false,
    submitBusy: false,
    blockedReason: "The check has not cleared yet.",
    trigger: 'onError with kind "rejected" — verdict REJECTED, score below 40',
  },
  {
    n: 7,
    id: "offline",
    title: "SDK failed to load",
    slot: "offline",
    tag: "Unavailable",
    status:
      "The check could not load — often an ad blocker or work network. Post anyway; a person reads it first.",
    alert: false,
    acts: [{ id: "reload", label: "Load it again", variant: "ghost" }],
    showWidget: false,
    submitLabel: "Post for review",
    submitEnabled: true,
    submitBusy: false,
    trigger: 'onError with kind "network" or "unsupported" — CORS, ad blocker, DNS filter, no WASM',
  },
  {
    n: 8,
    id: "expired",
    title: "Token expired",
    slot: "expired",
    tag: "Timed out",
    status:
      "The check expired while this page sat open and could not renew itself. Run it again to post.",
    alert: true,
    acts: [{ id: "rerun", label: "Run the check", variant: "ghost" }],
    showWidget: false,
    submitLabel: POST,
    submitEnabled: false,
    submitBusy: false,
    blockedReason: "The check expired. Run it again.",
    trigger:
      "responseToken older than its TTL at submit and the silent re-mount also failed to clear",
  },
  {
    n: 9,
    id: "limited",
    title: "Rate limited",
    slot: "limited",
    tag: "Paused",
    status: "You posted a moment ago. The wall opens to you again shortly.",
    alert: true,
    acts: [],
    showWidget: false,
    submitLabel: POST,
    submitEnabled: false,
    submitBusy: false,
    countdown: true,
    blockedReason: "The wall is paused for you right now.",
    trigger: 'onError with kind "rate-limited" (429 on /challenge), or 429 from the Worker',
  },
  {
    n: 10,
    id: "submitting",
    title: "Submit in flight",
    slot: "passed",
    tag: "Cleared",
    status: "Verified. Sending your post.",
    alert: false,
    acts: [],
    showWidget: false,
    submitLabel: "Posting",
    submitEnabled: false,
    submitBusy: true,
    blockedReason: "Already posting.",
    trigger: "POST to the Worker in flight, before the siteverify redeem returns",
  },
  {
    n: 11,
    id: "submitted",
    title: "Submitted",
    slot: "passive",
    tag: "Clear",
    status: "Nothing needed from you.",
    alert: false,
    acts: [],
    showWidget: false,
    submitLabel: POST,
    submitEnabled: true,
    submitBusy: false,
    note: { tag: "Posted", text: "It is at the top of the wall.", tone: "ok" },
    trigger: "Worker returned 201 after a successful siteverify redeem",
  },
  {
    n: 12,
    id: "rejected",
    title: "Server rejected",
    slot: "passive",
    tag: "Clear",
    status: "Nothing needed from you.",
    alert: false,
    acts: [],
    showWidget: false,
    submitLabel: POST,
    submitEnabled: true,
    submitBusy: false,
    note: {
      tag: "The wall",
      // Names which side said no: the wall, not the bot check.
      text: "The wall turned this one down — it repeats a post already up. Change the message and post again.",
      tone: "bad",
    },
    trigger: "Worker returned 422 — content rule, not a bot-defense verdict",
  },
];

export const byId = (id: StateId): StateSpec =>
  STATES.find((s) => s.id === id) as StateSpec;
